<template>
  <!-- AR Aging dashboard: open government receivables bucketed by age (OM Part 12.2) -->
  <v-container fluid>
    <h2>AR Aging — Government Accounts</h2>

    <v-row>
      <v-col
        v-for="bucket in aging.buckets"
        :key="bucket.key"
        cols="12"
        sm="6"
        md="3"
      >
        <v-card>
          <v-card-title :class="bucketTextClass(bucket.key)">
            {{ bucket.label }}
          </v-card-title>
          <v-card-text>
            <div class="text-h5 font-weight-bold">
              ₱{{ formatAmount(bucket.total) }}
            </div>
            <div class="caption">
              {{ bucket.count }} invoice{{ bucket.count === 1 ? "" : "s" }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card outlined>
          <v-card-text class="d-flex align-center py-2">
            <span class="font-weight-bold">
              Grand Total: ₱{{ formatAmount(aging.grand_total) }}
            </span>
            <v-spacer></v-spacer>
            <v-btn color="success" text @click="refresh" :loading="loading">
              <v-icon class="mr-2">mdi-refresh</v-icon>Refresh
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-text-field
      v-model="search"
      label="Search"
      prepend-icon="mdi-magnify"
      placeholder="Customer, invoice #, branch"
    ></v-text-field>

    <v-data-table
      :loading="loading"
      dense
      :items="allInvoices"
      :headers="headers"
      :search="search"
      :sort-by="['days_outstanding']"
      :sort-desc="[true]"
    >
      <template v-slot:item.balance="{ item }">
        <span class="error--text font-weight-bold">
          ₱{{ formatAmount(item.balance) }}
        </span>
      </template>
      <template v-slot:item.bucket="{ item }">
        <v-chip small :color="bucketColor(item.bucket)" class="white--text">
          {{ bucketLabel(item.bucket) }}
        </v-chip>
      </template>
      <template v-slot:item.last_follow_up="{ item }">
        <span v-if="item.latest_remark" :title="item.latest_remark.remark">
          {{ formatDateTime(item.latest_remark.created_at) }} —
          {{ item.latest_remark.user_name }}
        </span>
        <span v-else class="grey--text">No follow-up yet</span>
      </template>
      <template v-slot:item.action="{ item }">
        <v-btn small text color="primary" @click="openRemarks(item)">
          <v-icon small class="mr-1">mdi-note-text-outline</v-icon>
          Remarks
          <v-chip v-if="item.remarks_count" x-small class="ml-1">
            {{ item.remarks_count }}
          </v-chip>
        </v-btn>
      </template>
    </v-data-table>

    <!-- Follow-up remarks log -->
    <v-dialog v-model="remarks_dialog" max-width="600px">
      <v-card v-if="selected_invoice">
        <v-card-title>
          Follow-up Remarks — Invoice #{{ selected_invoice.invoice_code }}
        </v-card-title>
        <v-card-subtitle>
          {{ selected_invoice.customer_name }} · Balance ₱{{
            formatAmount(selected_invoice.balance)
          }}
          · {{ selected_invoice.days_outstanding }} days outstanding
        </v-card-subtitle>
        <v-card-text>
          <div v-if="remarks.length === 0" class="grey--text mb-4">
            No follow-ups logged yet.
          </div>
          <v-timeline v-else dense class="mb-4">
            <v-timeline-item
              v-for="remark in remarks"
              :key="remark.id"
              small
              color="primary"
            >
              <div class="caption grey--text">
                {{ formatDateTime(remark.created_at) }} —
                {{ remark.user ? remark.user.name : "" }}
              </div>
              <div>{{ remark.remark }}</div>
            </v-timeline-item>
          </v-timeline>
          <div v-if="can('ar.log_remark')">
            <v-textarea
              v-model="new_remark"
              label="Log a follow-up"
              rows="2"
              auto-grow
              outlined
              dense
              placeholder="e.g. Called the treasurer's office — promised payment next week"
            ></v-textarea>
            <v-btn
              color="primary"
              small
              :loading="saving_remark"
              :disabled="!new_remark.trim()"
              @click="saveRemark"
            >
              Add Remark
            </v-btn>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="remarks_dialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="caption grey--text text-right mt-2">ArAging.vue</div>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";

const BUCKET_META = {
  "1_month": { label: "1 Month", color: "green" },
  "2_months": { label: "2 Months", color: "orange" },
  "3_months": { label: "3 Months", color: "deep-orange" },
  beyond_3_months: { label: ">3 Months", color: "red" },
};

export default {
  name: "ArAging",
  data() {
    return {
      loading: false,
      search: "",
      headers: [
        { text: "", value: "action", sortable: false },
        { text: "Customer", value: "customer_name" },
        { text: "Branch", value: "branch_name" },
        { text: "Invoice #", value: "invoice_code" },
        { text: "Invoice Date", value: "invoice_date" },
        { text: "Days", value: "days_outstanding" },
        { text: "Balance", value: "balance" },
        { text: "Bucket", value: "bucket" },
        {
          text: "Last Follow-up",
          value: "last_follow_up",
          sortable: false,
        },
      ],
      remarks_dialog: false,
      selected_invoice: null,
      new_remark: "",
      saving_remark: false,
    };
  },
  computed: {
    ...mapGetters({
      aging: "ar/aging",
      remarks: "ar/remarks",
      hasPermission: "permission/hasPermission",
    }),
    allInvoices() {
      return this.aging.buckets.flatMap((bucket) => bucket.invoices);
    },
  },
  created() {
    this.refresh();
  },
  methods: {
    can(key) {
      return this.hasPermission(key);
    },
    refresh() {
      this.loading = true;
      this.get_ar_aging()
        .catch(() => {
          this.$alert({
            title: "Error",
            message: "Failed to load the AR aging dashboard.",
            color: "error",
            icon: "mdi-alert",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openRemarks(invoice) {
      this.selected_invoice = invoice;
      this.new_remark = "";
      this.remarks_dialog = true;
      this.get_ar_remarks(invoice.id);
    },
    saveRemark() {
      this.saving_remark = true;
      this.add_ar_remark({
        invoice_id: this.selected_invoice.id,
        remark: this.new_remark.trim(),
      })
        .then(() => {
          this.new_remark = "";
          this.selected_invoice.remarks_count++;
          this.$alert({
            title: "Saved",
            message: "Follow-up remark logged.",
            color: "success",
            icon: "mdi-check",
          });
        })
        .catch(() => {
          this.$alert({
            title: "Error",
            message: "Failed to save the remark.",
            color: "error",
            icon: "mdi-alert",
          });
        })
        .finally(() => {
          this.saving_remark = false;
        });
    },
    bucketLabel(key) {
      return BUCKET_META[key] ? BUCKET_META[key].label : key;
    },
    bucketColor(key) {
      return BUCKET_META[key] ? BUCKET_META[key].color : "grey";
    },
    bucketTextClass(key) {
      return `${this.bucketColor(key)}--text`;
    },
    formatAmount(value) {
      return Number(value || 0).toLocaleString();
    },
    formatDateTime(value) {
      return moment(value).format("MMM D, YYYY h:mm A");
    },
    ...mapActions({
      get_ar_aging: "ar/get_ar_aging",
      get_ar_remarks: "ar/get_ar_remarks",
      add_ar_remark: "ar/add_ar_remark",
    }),
  },
};
</script>
